import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import {
  RexApiException,
  RexListingRefreshResultStatus,
  useInvalidateUseGetActiveListingsForAgency,
  useInvalidateUseGetListingByCampaignId,
  useInvalidateUseStaffGetRexListingRefreshStatus,
  useStaffGetRexListingRefreshStatus,
  useStaffRefreshRexListing,
  type RefreshRexListingRequest,
  type RexListingRefreshResult,
  type StaffGetRexListingRefreshStatusQueryKey,
} from '@urbanx/agx.rex.client';
import { useEffect, useRef, useState } from 'react';
import { RexListingRefreshForm } from './rexListingRefreshFormDefinition';
import {
  clearSavedRexListingRefresh,
  readSavedRexListingRefresh,
  saveRexListingRefresh,
} from './rexListingRefreshStorage';

export function useRexListingRefresh() {
  const queryClient = useQueryClient();
  const [request, setRequest] = useState(readSavedRexListingRefresh);
  const [requestError, setRequestError] = useState('');
  const lastInvalidated = useRef('');
  const invalidateStatus = useInvalidateUseStaffGetRexListingRefreshStatus();
  const invalidateListings = useInvalidateUseGetActiveListingsForAgency();
  const invalidateCampaignListing = useInvalidateUseGetListingByCampaignId();
  const { staffRefreshRexListing, staffRefreshRexListingPending: isRefreshing } =
    useStaffRefreshRexListing();
  const { data: result, isError: statusError } = useStaffGetRexListingRefreshStatus(
    request?.agencyId ?? '',
    request?.operationId ?? '',
    !!request && !isRefreshing
  );

  const statusQueryKey: StaffGetRexListingRefreshStatusQueryKey = [
    'staffGetRexListingRefreshStatus',
    request?.agencyId ?? '',
    request?.operationId ?? '',
  ];
  const isCheckingStatus = useIsFetching({ queryKey: statusQueryKey, exact: true }) > 0;

  useEffect(() => {
    if (
      result &&
      result.status !== RexListingRefreshResultStatus.Running &&
      readSavedRexListingRefresh()?.operationId === result.operationId
    ) {
      // Keep the result on this page, but only unresolved requests need recovery after a reload.
      clearSavedRexListingRefresh();
    }
  }, [result]);

  function rememberRequest(next: RefreshRexListingRequest) {
    saveRexListingRefresh(next);
    setRequest(next);
  }

  async function runRefresh(next: RefreshRexListingRequest) {
    rememberRequest(next);
    setRequestError('');

    try {
      const response = await staffRefreshRexListing(next);
      if (!response) throw new Error('No refresh result was returned.');

      rememberRequest({ ...next, operationId: response.operationId });
      const key: StaffGetRexListingRefreshStatusQueryKey = [
        'staffGetRexListingRefreshStatus',
        next.agencyId,
        response.operationId,
      ];
      queryClient.setQueryData<RexListingRefreshResult>(key, response);
      return response;
    } catch (error) {
      if (
        typeof error === 'string' ||
        (error instanceof RexApiException && [400, 401, 403, 404, 409].includes(error.status))
      ) {
        clearSavedRexListingRefresh();
        setRequest(null);
        setRequestError(
          typeof error === 'string'
            ? error
            : 'This refresh request was rejected. Check the listing and your permissions.'
        );
        return null;
      }

      setRequestError(
        'The request outcome could not be confirmed. Check status before starting another refresh, or retry this same request.'
      );
      await invalidateStatus(next.agencyId, next.operationId);
      return null;
    }
  }

  const isUnresolved =
    !!request && (!result || result.status === RexListingRefreshResultStatus.Running);

  const isCompleted = result?.status === RexListingRefreshResultStatus.Completed;

  const form = RexListingRefreshForm.useForm({
    defaultValues: {
      agencyId: isCompleted ? '' : (request?.agencyId ?? ''),
      listingId: isCompleted ? '' : (request?.listingId?.toString() ?? ''),
    },
    disableWhileSubmitting: true,
    onSubmit: async ({ parsedValues }) => {
      // Native form submission (including Enter) must preserve the saved recovery request.
      if (isUnresolved || isRefreshing)
        return { status: 'failed', error: new Error('Check the saved operation status.') };

      const response = await runRefresh({
        operationId: crypto.randomUUID(),
        agencyId: parsedValues.agencyId,
        listingId: Number(parsedValues.listingId),
      });
      if (!response)
        return { status: 'failed', error: new Error('Check the saved operation status.') };

      return response.status === RexListingRefreshResultStatus.Completed
        ? { status: 'accepted', baseline: { agencyId: '', listingId: '' } }
        : { status: 'accepted' };
    },
  });

  useEffect(() => {
    if (
      result?.status !== RexListingRefreshResultStatus.Completed ||
      lastInvalidated.current === result.operationId
    )
      return;

    lastInvalidated.current = result.operationId;
    // Status recovery can confirm completion even when the original request lost its response.
    form.accept({ agencyId: '', listingId: '' });
    void Promise.all([
      invalidateListings(result.agencyId),
      invalidateCampaignListing(result.campaignId),
    ]);
  }, [result, form, invalidateListings, invalidateCampaignListing]);

  const canCheckStatus = isUnresolved && !isRefreshing;

  return {
    form,
    request,
    result,
    isRefreshing,
    isUnresolved,
    isCheckingStatus,
    isCheckingOutcome: isRefreshing || isCheckingStatus,
    requestError:
      !result || result.status === RexListingRefreshResultStatus.Running ? requestError : '',
    statusError:
      statusError && !requestError
        ? 'Unable to retrieve the saved refresh result. Check status again.'
        : '',
    canCheckStatus,
    canRetrySameRequest: canCheckStatus && !isCheckingStatus && !result && statusError,
    checkStatus: () => request && invalidateStatus(request.agencyId, request.operationId),
    retrySameRequest: () => request && runRefresh(request),
  };
}
